import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Button, Container, Grid, Typography, useMediaQuery } from "@mui/material";
import CartTable from "components/molecules/tables/CartTable";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch, useSelector } from "react-redux";
import { deleteArticles } from "reducers/cartReducer";
import type { StoreProps } from "store/store";
import type CommandeFormModel from "models/CommandeFormModel";
import commandesService from "services/commandesService";
import type CommandeArticleCartModel from "models/CommandeArticleCartModel";
import { enqueueSnackbar } from "notistack";
import ApplicationRoles from "constants/ApplicationRoles";
import { useUser, dateUtil, Authorization } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import graphService from "services/graphService";
import AgentSelect from "components/atoms/AgentSelect";
import MagasinDeRetrait from "constants/MagasinDeRetrait";
import { useNavigate } from "react-router";
import { routesConfig } from "config/app-config";
import PageTitle from "components/molecules/PageTitle";
import MagasinDeRetraitInput from "components/atoms/inputs/MagasinDeRetraitInput";
import ToastMessages from "constants/ToastMessages";

function CartPage(): ReactNode {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [magasin, setMagasin] = useState<string>("");
  const [agents, setAgents] = useState<Array<User>>();
  const { user } = useUser();
  const [selectedAgent, setSelectedAgent] = useState<User | undefined>(user);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const cart = useSelector<StoreProps, Array<CommandeArticleCartModel>>((state) => state?.cart);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  async function sendCommand(): Promise<void> {
    if (selectedAgent == null) {
      return;
    }
    setIsSending(true);
    try {
      const commande: CommandeFormModel = {
        articles: cart,
        magasinDeRetrait: magasin,
      };

      if (user?.id !== selectedAgent.id) {
        await commandesService.createForAgent(commande, selectedAgent.id);
      } else {
        await commandesService.create(commande);
      }

      dispatch(deleteArticles());
      enqueueSnackbar(ToastMessages.ENVOI_COMMANDE_SUCESS, { variant: "success" });

      navigate(routesConfig.userCommandes.path);
    } catch (e) {
      enqueueSnackbar(ToastMessages.ERROR_RETRY, { variant: "error" });
    } finally {
      setIsSending(false);
    }
  }

  useEffect(() => {
    async function getLastMagasin(): Promise<void> {
      const _mesCommandes = await commandesService.getAllByMe();
      _mesCommandes.sort((a, b) => dateUtil.compareDesc(a.dateCommande, b.dateCommande));
      const lastMagasin = _mesCommandes[0].magasinDeRetrait ?? "";

      if (Object.values(MagasinDeRetrait).includes(lastMagasin as MagasinDeRetrait)) {
        setMagasin(lastMagasin);
      }
    }

    async function getAgents(): Promise<void> {
      setAgents(await graphService.getAllAgents());
    }

    void getAgents();
    void getLastMagasin();
  }, []);

  return (
    <>
      <PageTitle title="Votre Panier" />
      <Container maxWidth="md">
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid size={12}>
            <CartTable />
          </Grid>
          <Grid container spacing={2} size={12}>
            <Grid size={12}>
              <Typography variant={isSmallScreen ? "h4" : "h3"} color="primary">
                Magasin de retrait :
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MagasinDeRetraitInput
                unique
                selectedCentreRetrait={magasin}
                setSelectedCentreRetrait={setMagasin}
                helperText="* Sélectionner un magasin de retrait est obligatoire pour passer commande."
                required
              />
            </Grid>
          </Grid>
          <Authorization roles={ApplicationRoles.SSTMANAGER}>
            <Grid container spacing={2} size={12}>
              <Grid size={12}>
                <Typography variant={isSmallScreen ? "h4" : "h3"} color="primary">
                  Commander les articles pour :
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <AgentSelect
                  size="small"
                  agents={agents ?? []}
                  value={selectedAgent}
                  onValueChange={setSelectedAgent}
                />
              </Grid>
            </Grid>
          </Authorization>
          <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={cart.length < 1 || magasin === "" || isSending}
              onClick={() => {
                void sendCommand();
              }}
              variant="contained"
              endIcon={<SendIcon />}
              loading={isSending}>
              Commander
            </Button>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default CartPage;
